import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Users, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Mail, 
  Phone, 
  Globe, 
  MapPin,
  Eye,
  FileText,
  Loader2,
  Image,
  Euro,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AdminGuideApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  const loadApplications = async () => {
    try {
      const params = statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await adminAPI.getGuideApplications(params);
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadApplications();
  }, [statusFilter]);
  
  const handleApprove = async (app, force = false) => {
    if (!app.email_verified && !force) {
      // Show confirmation dialog for force approve
      if (window.confirm('This applicant has not verified their email. Do you want to approve anyway? The welcome email will be sent to their registered email address.')) {
        handleApprove(app, true);
      }
      return;
    }
    
    setProcessing(true);
    try {
      await adminAPI.approveGuideApplication(app.id, force);
      toast.success('Application approved! Guide account created and welcome email sent.');
      loadApplications();
      setDetailsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleVerifyEmail = async (app) => {
    setProcessing(true);
    try {
      await adminAPI.verifyApplicationEmail(app.id);
      toast.success('Email verified successfully!');
      loadApplications();
      // Update the selected app if viewing details
      if (selectedApp && selectedApp.id === app.id) {
        setSelectedApp({...selectedApp, email_verified: true});
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify email');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      await adminAPI.rejectGuideApplication(selectedApp.id, rejectReason);
      toast.success('Application rejected.');
      loadApplications();
      setRejectOpen(false);
      setDetailsOpen(false);
      setRejectReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject application');
    } finally {
      setProcessing(false);
    }
  };
  
  const openDetails = (app) => {
    setSelectedApp(app);
    setDetailsOpen(true);
  };
  
  const openRejectDialog = (app) => {
    setSelectedApp(app);
    setRejectOpen(true);
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };
  
  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
  };
  
  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Guide Applications</h1>
            <p className="text-slate-500">Review and manage guide registration requests</p>
          </div>
          <Button variant="outline" onClick={loadApplications}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('all')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'pending' ? 'ring-2 ring-amber-500' : ''}`} onClick={() => setStatusFilter('pending')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'approved' ? 'ring-2 ring-green-500' : ''}`} onClick={() => setStatusFilter('approved')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'rejected' ? 'ring-2 ring-red-500' : ''}`} onClick={() => setStatusFilter('rejected')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Rejected</p>
                  <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle>Applications</CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No applications found
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Applicant</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Languages</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Verification</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((app) => (
                    <TableRow key={app.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {app.profile_photo_url ? (
                            <img 
                              src={`${API_BASE}${app.profile_photo_url}`} 
                              alt={app.full_name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                              <Users className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{app.full_name}</p>
                            <p className="text-sm text-slate-500">{app.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          {app.city}, {app.country}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {app.languages.slice(0, 2).map(lang => (
                            <Badge key={lang} variant="outline" className="text-xs">{lang}</Badge>
                          ))}
                          {app.languages.length > 2 && (
                            <Badge variant="outline" className="text-xs">+{app.languages.length - 2}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">€{app.hourly_rate_eur}/hr</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {app.email_verified ? (
                            <Badge className="bg-green-100 text-green-700 text-xs"><Mail className="w-3 h-3 mr-1" />✓</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500 text-xs"><Mail className="w-3 h-3 mr-1" />✗</Badge>
                          )}
                          {app.phone_verified ? (
                            <Badge className="bg-green-100 text-green-700 text-xs"><Phone className="w-3 h-3 mr-1" />✓</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500 text-xs"><Phone className="w-3 h-3 mr-1" />✗</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(app.status)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(app.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openDetails(app)} data-testid={`view-btn-${app.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {app.status === 'pending' && (
                            <>
                              {!app.email_verified && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                  onClick={() => handleVerifyEmail(app)}
                                  disabled={processing}
                                  title="Manually verify email"
                                  data-testid={`verify-email-btn-${app.id}`}
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                className={app.email_verified ? "bg-green-600 hover:bg-green-700" : "bg-amber-500 hover:bg-amber-600"}
                                onClick={() => handleApprove(app)}
                                disabled={processing}
                                title={app.email_verified ? "Approve application" : "Force approve (email not verified)"}
                                data-testid={`approve-btn-${app.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => openRejectDialog(app)}
                                data-testid={`reject-btn-${app.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
            <DialogDescription>
              Submitted on {selectedApp && new Date(selectedApp.created_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-6 mt-4">
              {/* Profile Header */}
              <div className="flex items-start gap-4">
                {selectedApp.profile_photo_url ? (
                  <img 
                    src={`${API_BASE}${selectedApp.profile_photo_url}`} 
                    alt={selectedApp.full_name}
                    className="w-24 h-24 rounded-xl object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-xl bg-slate-200 flex items-center justify-center">
                    <Users className="w-10 h-10 text-slate-400" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-xl font-bold">{selectedApp.full_name}</h3>
                  <div className="flex items-center gap-4 text-sm text-slate-600 mt-1">
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" /> {selectedApp.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" /> {selectedApp.phone}
                    </span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    {getStatusBadge(selectedApp.status)}
                    {selectedApp.email_verified && (
                      <Badge className="bg-green-100 text-green-700">Email Verified</Badge>
                    )}
                    {selectedApp.phone_verified && (
                      <Badge className="bg-green-100 text-green-700">Phone Verified</Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                    <MapPin className="w-4 h-4" /> Location
                  </div>
                  <p className="font-medium">{selectedApp.city}, {selectedApp.country}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                    <Euro className="w-4 h-4" /> Hourly Rate
                  </div>
                  <p className="font-medium">€{selectedApp.hourly_rate_eur}/hour</p>
                </div>
              </div>
              
              {/* Languages */}
              <div>
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                  <Globe className="w-4 h-4" /> Languages
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedApp.languages.map(lang => (
                    <Badge key={lang} variant="outline">{lang}</Badge>
                  ))}
                </div>
              </div>
              
              {/* Monuments */}
              <div>
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                  <MapPin className="w-4 h-4" /> Monuments
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedApp.monument_names?.map((name, idx) => (
                    <Badge key={idx}>{name}</Badge>
                  ))}
                </div>
              </div>
              
              {/* About */}
              <div>
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                  <FileText className="w-4 h-4" /> About
                </div>
                <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">{selectedApp.about_me}</p>
              </div>
              
              {/* Documents */}
              <div>
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                  <FileText className="w-4 h-4" /> Documents
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {selectedApp.license_number && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">License Number</p>
                      <p className="font-medium">{selectedApp.license_number}</p>
                    </div>
                  )}
                  {selectedApp.license_document_url && (
                    <a 
                      href={`${API_BASE}${selectedApp.license_document_url}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-3 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                    >
                      <p className="text-sm text-indigo-600">License Document</p>
                      <p className="font-medium text-indigo-700">View →</p>
                    </a>
                  )}
                  {selectedApp.badge_document_url && (
                    <a 
                      href={`${API_BASE}${selectedApp.badge_document_url}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-3 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                    >
                      <p className="text-sm text-indigo-600">Badge Document</p>
                      <p className="font-medium text-indigo-700">View →</p>
                    </a>
                  )}
                </div>
              </div>
              
              {/* Actions */}
              {selectedApp.status === 'pending' && (
                <div className="space-y-3 pt-4 border-t">
                  {!selectedApp.email_verified && (
                    <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <Mail className="w-5 h-5 text-amber-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800">Email not verified</p>
                        <p className="text-xs text-amber-600">You can manually verify or force approve</p>
                      </div>
                      <Button 
                        size="sm"
                        variant="outline"
                        className="border-amber-400 text-amber-700 hover:bg-amber-100"
                        onClick={() => handleVerifyEmail(selectedApp)}
                        disabled={processing}
                        data-testid="modal-verify-email-btn"
                      >
                        Verify Email
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <Button 
                      className={`flex-1 ${selectedApp.email_verified ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-500 hover:bg-amber-600'}`}
                      onClick={() => handleApprove(selectedApp)}
                      disabled={processing}
                      data-testid="modal-approve-btn"
                    >
                      {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      {selectedApp.email_verified ? 'Approve & Create Account' : 'Force Approve'}
                    </Button>
                    <Button 
                      variant="destructive"
                      className="flex-1"
                      onClick={() => openRejectDialog(selectedApp)}
                      disabled={processing}
                      data-testid="modal-reject-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject Application
                    </Button>
                  </div>
                </div>
              )}
              
              {selectedApp.status === 'rejected' && selectedApp.rejection_reason && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-600 font-medium">Rejection Reason:</p>
                  <p className="text-red-700 mt-1">{selectedApp.rejection_reason}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Application</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this application. This will be sent to the applicant.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Rejection Reason</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this application is being rejected..."
                rows={4}
                data-testid="reject-reason-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setRejectOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                className="flex-1"
                onClick={handleReject}
                disabled={processing || !rejectReason.trim()}
                data-testid="confirm-reject-btn"
              >
                {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                Reject Application
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminGuideApplications;
